<template>
  <div class="branch-form-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Filial Autorizada <span class="page-subtitle">{{ isEdit ? 'Editando' : '' }}</span></h1>
        <span v-if="form.uf" class="uf-badge">{{ form.uf }}</span>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="voltar">
          <i class="ti ti-arrow-left" aria-hidden="true"></i>
          Voltar
        </button>
        <button type="button" class="btn btn-primary" @click="salvar" :disabled="loading">
          <i class="ti ti-device-floppy" aria-hidden="true"></i>
          {{ loading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>

    <!-- Seção 1: Identificação -->
    <div class="section">
      <div class="section-head">
        <i class="ti ti-building-school" aria-hidden="true"></i>
        <h3>Identificação</h3>
      </div>
      <div class="section-body">
        <div class="grid-3">
          <div class="field" style="grid-column: span 2;">
            <label for="nome-escola">Nome da Escola</label>
            <input id="nome-escola" type="text" v-model="form.name" required />
          </div>
          <div class="field">
            <label for="codigo-inep">Código INEP</label>
            <input id="codigo-inep" type="text" v-model="form.inep_code" />
          </div>
          <div class="field" style="grid-column: span 2;">
            <label for="cnpj">CNPJ</label>
            <TheMask
              id="cnpj"
              type="text"
              :mask="['##.###.###/####-##']"
              v-model="form.cnpj"
              :masked="true"
            />
          </div>
          <div class="field">
            <label for="uf">UF</label>
            <select id="uf" v-model="form.uf">
              <option disabled value="">-- UF --</option>
              <option v-for="(opt, index) in uf" :key="index" :value="opt.sigla">
                {{ opt.sigla }}
              </option>
            </select>
          </div>
          <div class="field" style="grid-column: span 2;">
            <label for="endereco">Endereço</label>
            <input id="endereco" type="text" v-model="form.address" />
          </div>
          <div class="field">
            <label for="cidade">Cidade</label>
            <input id="cidade" type="text" v-model="form.city" />
          </div>
        </div>
      </div>
    </div>

    <!-- Seção 2: Responsável & Contato -->
    <div class="section">
      <div class="section-head">
        <i class="ti ti-user-circle" aria-hidden="true"></i>
        <h3>Responsável & Contato</h3>
      </div>
      <div class="section-body">
        <div class="grid-3">
          <div class="field">
            <label for="responsavel">Responsável</label>
            <input id="responsavel" type="text" v-model="form.responsible" />
          </div>
          <div class="field">
            <label for="email">E-mail</label>
            <input id="email" type="email" v-model="form.email" />
          </div>
          <div class="field">
            <label for="telefone">Telefone</label>
            <TheMask
              id="telefone"
              type="text"
              :mask="['(##) #####-####']"
              v-model="form.phone"
              :masked="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Seção 3: Resolução -->
    <div class="section">
      <div class="section-head">
        <i class="ti ti-file-certificate" aria-hidden="true"></i>
        <h3>Resolução</h3>
      </div>
      <div class="section-body">
        <div class="grid-2">
          <div class="field">
            <label for="resolucao">Resolução</label>
            <textarea id="resolucao" v-model="form.resolution" rows="2" style="height: 52px;" placeholder="Texto da resolução que autoriza a escola"></textarea>
          </div>
          <div class="field">
            <label for="validade">Validade da Resolução</label>
            <input id="validade" type="date" v-model="form.resolution_expires_at" />
          </div>
        </div>
      </div>
    </div>

    <!-- Seção 4: Imagens & Documentos -->
    <div class="section">
      <div class="section-head">
        <i class="ti ti-photo" aria-hidden="true"></i>
        <h3>Imagens & Documentos</h3>
      </div>
      <div class="section-body">
        <!-- Bloco de Logotipos -->
        <div class="subsection">
          <h4 class="subsection-title">Logotipos</h4>
          <div class="grid-2">
            <!-- Logo Colorida -->
            <div class="image-slot">
              <label>Colorida</label>
              <div class="image-preview">
                <img v-if="form.imagens.logoColorida" :src="form.imagens.logoColorida.url || form.imagens.logoColorida" alt="Logo Colorida" />
                <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png"
                style="display: none"
                ref="inputLogoColorida"
                @change="handleFileChange($event, 'logoColorida')"
              />
              <div class="image-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  @click="triggerUpload('inputLogoColorida')"
                >
                  {{ form.imagens.logoColorida ? 'Trocar' : 'Enviar' }}
                </button>
                <button
                  v-if="form.imagens.logoColorida"
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click="removerImagem('logoColorida')"
                >
                  Remover
                </button>
              </div>
              <small class="image-constraint">JPG/PNG, máx. 2MB</small>
            </div>

            <!-- Logo Preto e Branco -->
            <div class="image-slot">
              <label>Preto e Branco</label>
              <div class="image-preview">
                <img v-if="form.imagens.logoPretoBranco" :src="form.imagens.logoPretoBranco.url || form.imagens.logoPretoBranco" alt="Logo P&B" />
                <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png"
                style="display: none"
                ref="inputLogoPretoBranco"
                @change="handleFileChange($event, 'logoPretoBranco')"
              />
              <div class="image-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  @click="triggerUpload('inputLogoPretoBranco')"
                >
                  {{ form.imagens.logoPretoBranco ? 'Trocar' : 'Enviar' }}
                </button>
                <button
                  v-if="form.imagens.logoPretoBranco"
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click="removerImagem('logoPretoBranco')"
                >
                  Remover
                </button>
              </div>
              <small class="image-constraint">JPG/PNG, máx. 2MB</small>
            </div>
          </div>
        </div>

        <!-- Bloco de Documentos -->
        <div class="subsection">
          <h4 class="subsection-title">Documentos</h4>
          <div class="document-grid">
            <!-- Card: Certificado EJA -->
            <div class="document-card">
              <div class="document-card-header">
                <div class="document-card-title">
                  <i class="ti ti-id-badge" aria-hidden="true"></i>
                  Certificado EJA
                </div>
                <span class="document-badge">JPG/PNG · 4MB</span>
              </div>
              <div class="document-card-body">
                <div class="grid-2">
                  <!-- Frente -->
                  <div class="image-slot">
                    <label>Fundo – Frente</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.certificadoEjaFrente" :src="form.imagens.certificadoEjaFrente.url || form.imagens.certificadoEjaFrente" alt="Certificado EJA Frente" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCertificadoEjaFrente"
                      @change="handleFileChange($event, 'certificadoEjaFrente')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCertificadoEjaFrente')"
                      >
                        {{ form.imagens.certificadoEjaFrente ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.certificadoEjaFrente"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('certificadoEjaFrente')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>

                  <!-- Verso -->
                  <div class="image-slot">
                    <label>Fundo – Verso</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.certificadoEjaVerso" :src="form.imagens.certificadoEjaVerso.url || form.imagens.certificadoEjaVerso" alt="Certificado EJA Verso" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCertificadoEjaVerso"
                      @change="handleFileChange($event, 'certificadoEjaVerso')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCertificadoEjaVerso')"
                      >
                        {{ form.imagens.certificadoEjaVerso ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.certificadoEjaVerso"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('certificadoEjaVerso')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card: Certificado Técnico -->
            <div class="document-card">
              <div class="document-card-header">
                <div class="document-card-title">
                  <i class="ti ti-certificate" aria-hidden="true"></i>
                  Certificado Técnico
                </div>
                <span class="document-badge">JPG/PNG · 4MB</span>
              </div>
              <div class="document-card-body">
                <div class="grid-2">
                  <!-- Frente -->
                  <div class="image-slot">
                    <label>Fundo – Frente</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.certificadoTecnicoFrente" :src="form.imagens.certificadoTecnicoFrente.url || form.imagens.certificadoTecnicoFrente" alt="Certificado Técnico Frente" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCertificadoTecnicoFrente"
                      @change="handleFileChange($event, 'certificadoTecnicoFrente')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCertificadoTecnicoFrente')"
                      >
                        {{ form.imagens.certificadoTecnicoFrente ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.certificadoTecnicoFrente"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('certificadoTecnicoFrente')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>

                  <!-- Verso -->
                  <div class="image-slot">
                    <label>Fundo – Verso</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.certificadoTecnicoVerso" :src="form.imagens.certificadoTecnicoVerso.url || form.imagens.certificadoTecnicoVerso" alt="Certificado Técnico Verso" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCertificadoTecnicoVerso"
                      @change="handleFileChange($event, 'certificadoTecnicoVerso')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCertificadoTecnicoVerso')"
                      >
                        {{ form.imagens.certificadoTecnicoVerso ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.certificadoTecnicoVerso"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('certificadoTecnicoVerso')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card: Histórico -->
            <div class="document-card">
              <div class="document-card-header">
                <div class="document-card-title">
                  <i class="ti ti-history" aria-hidden="true"></i>
                  Histórico
                </div>
                <span class="document-badge">JPG/PNG · 4MB</span>
              </div>
              <div class="document-card-body">
                <div class="grid-2">
                  <!-- Frente -->
                  <div class="image-slot">
                    <label>Fundo – Frente</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.historicoFrente" :src="form.imagens.historicoFrente.url || form.imagens.historicoFrente" alt="Histórico Frente" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputHistoricoFrente"
                      @change="handleFileChange($event, 'historicoFrente')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputHistoricoFrente')"
                      >
                        {{ form.imagens.historicoFrente ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.historicoFrente"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('historicoFrente')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>

                  <!-- Verso (opcional) -->
                  <div class="image-slot">
                    <label>Fundo – Verso <span class="badge-optional">opcional</span></label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.historicoVerso" :src="form.imagens.historicoVerso.url || form.imagens.historicoVerso" alt="Histórico Verso" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputHistoricoVerso"
                      @change="handleFileChange($event, 'historicoVerso')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputHistoricoVerso')"
                      >
                        {{ form.imagens.historicoVerso ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.historicoVerso"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('historicoVerso')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card: Certificado Cursos Livres -->
            <div class="document-card">
              <div class="document-card-header">
                <div class="document-card-title">
                  <i class="ti ti-certificate" aria-hidden="true"></i>
                  Certificado Cursos Livres
                </div>
                <span class="document-badge">JPG/PNG · 4MB</span>
              </div>
              <div class="document-card-body">
                <div class="grid-2">
                  <!-- Frente -->
                  <div class="image-slot">
                    <label>Fundo – Frente</label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.cursosLivresFrente" :src="form.imagens.cursosLivresFrente.url || form.imagens.cursosLivresFrente" alt="Cursos Livres Frente" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCursosLivresFrente"
                      @change="handleFileChange($event, 'cursosLivresFrente')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCursosLivresFrente')"
                      >
                        {{ form.imagens.cursosLivresFrente ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.cursosLivresFrente"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('cursosLivresFrente')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>

                  <!-- Verso (opcional) -->
                  <div class="image-slot">
                    <label>Fundo – Verso <span class="badge-optional">opcional</span></label>
                    <div class="image-preview image-preview-small">
                      <img v-if="form.imagens.cursosLivresVerso" :src="form.imagens.cursosLivresVerso.url || form.imagens.cursosLivresVerso" alt="Cursos Livres Verso" />
                      <i v-else class="ti ti-photo-off" aria-hidden="true"></i>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style="display: none"
                      ref="inputCursosLivresVerso"
                      @change="handleFileChange($event, 'cursosLivresVerso')"
                    />
                    <div class="image-actions">
                      <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="triggerUpload('inputCursosLivresVerso')"
                      >
                        {{ form.imagens.cursosLivresVerso ? 'Trocar' : 'Enviar' }}
                      </button>
                      <button
                        v-if="form.imagens.cursosLivresVerso"
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removerImagem('cursosLivresVerso')"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card de Adição -->
            <div class="document-card document-card-add" @click="adicionarTipoDocumento">
              <i class="ti ti-plus" aria-hidden="true"></i>
              <span>Adicionar tipo de documento</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="page-footer">
      <button type="button" class="btn btn-secondary" @click="voltar">
        <i class="ti ti-arrow-left" aria-hidden="true"></i>
        Voltar
      </button>
      <button type="button" class="btn btn-primary" @click="salvar" :disabled="loading">
        <i class="ti ti-device-floppy" aria-hidden="true"></i>
        {{ loading ? 'Salvando...' : 'Salvar' }}
      </button>
    </div>
  </div>
</template>

<script>
import api from "@/services";
import StatesMixin from "@/mixins/StatesMixin";

export default {
  name: "BranchForm",
  mixins: [StatesMixin],
  props: ["id"],
  data() {
    return {
      loading: false,
      files: {},
      form: {
        name: "",
        cnpj: "",
        address: "",
        city: "",
        uf: "",
        responsible: "",
        email: "",
        phone: "",
        inep_code: "",
        resolution: "",
        resolution_expires_at: "",
        imagens: {
          logoColorida: null,
          logoPretoBranco: null,
          certificadoEjaFrente: null,
          certificadoEjaVerso: null,
          certificadoTecnicoFrente: null,
          certificadoTecnicoVerso: null,
          historicoFrente: null,
          historicoVerso: null,
          cursosLivresFrente: null,
          cursosLivresVerso: null,
        },
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
  },
  methods: {
    formatDateForInput(dateString) {
      // Input type="date" requer formato YYYY-MM-DD
      if (!dateString) return '';
      
      // Se já está no formato correto (YYYY-MM-DD), retornar
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Se contém timestamp (ISO 8601), extrair apenas a data
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      
      // Se está em formato brasileiro (DD/MM/YYYY), converter
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month}-${day}`;
      }
      
      return dateString;
    },
    triggerUpload(refName) {
      this.$refs[refName].click();
    },
    handleFileChange(event, campo) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Armazenar arquivo para upload
      this.files[campo] = file;
      
      // Criar preview local
      const url = URL.createObjectURL(file);
      this.$set(this.form.imagens, campo, { url, file });
      
      // Resetar input para permitir re-seleção do mesmo arquivo
      event.target.value = '';
    },
    async removerImagem(campo) {
      // Mapear campos do novo modelo para o modelo da API
      const apiFieldMap = {
        logoColorida: 'colored_logo',
        logoPretoBranco: 'bw_logo',
        certificadoEjaFrente: 'certificate_front_background',
        certificadoEjaVerso: 'certificate_back_background',
        certificadoTecnicoFrente: 'certificate_technical_front_background',
        certificadoTecnicoVerso: 'certificate_technical_back_background',
        historicoFrente: 'history_front_background',
        historicoVerso: 'history_back_background',
        cursosLivresFrente: 'certificate_free_front_background',
        cursosLivresVerso: 'certificate_free_back_background',
      };
      
      if (this.id && typeof this.form.imagens[campo] === 'string') {
        // Imagem existente no servidor
        try {
          await api.delete(`/branches/${this.id}/images/${apiFieldMap[campo]}`);
          Toast.fire("Imagem removida!", "", "success");
        } catch (error) {
          Toast.fire("Erro", error.response?.data?.message || "Erro ao remover imagem.", "error");
          return;
        }
      }
      
      // Remover do estado local
      this.$set(this.form.imagens, campo, null);
      delete this.files[campo];
    },
    adicionarTipoDocumento() {
      // Emit evento para futuras funcionalidades
      this.$emit('add-document-type');
      Toast.fire("Funcionalidade em desenvolvimento", "", "info");
    },
    buildFormData() {
      const fd = new FormData();
      
      // Campos de texto (exceto data)
      const textFields = [
        "name", "cnpj", "address", "city", "uf",
        "responsible", "email", "phone", "inep_code", "resolution",
      ];
      textFields.forEach((f) => {
        if (this.form[f] != null && this.form[f] !== '') {
          fd.append(f, this.form[f]);
        }
      });
      
      // Campo de data - só enviar se tiver valor válido
      if (this.form.resolution_expires_at && this.form.resolution_expires_at !== '') {
        fd.append('resolution_expires_at', this.form.resolution_expires_at);
      }
      
      // Mapear campos de imagem do novo modelo para o modelo da API
      const imageFieldMap = {
        logoColorida: 'colored_logo',
        logoPretoBranco: 'bw_logo',
        certificadoEjaFrente: 'certificate_front_background',
        certificadoEjaVerso: 'certificate_back_background',
        certificadoTecnicoFrente: 'certificate_technical_front_background',
        certificadoTecnicoVerso: 'certificate_technical_back_background',
        historicoFrente: 'history_front_background',
        historicoVerso: 'history_back_background',
        cursosLivresFrente: 'certificate_free_front_background',
        cursosLivresVerso: 'certificate_free_back_background',
      };
      
      // Anexar arquivos de imagem
      Object.keys(imageFieldMap).forEach((newField) => {
        const apiField = imageFieldMap[newField];
        if (this.files[newField]) {
          fd.append(apiField, this.files[newField]);
        }
      });
      
      return fd;
    },
    async getItem() {
      try {
        const res = await api.get(`/branches/${this.id}`);
        const data = res.data.data || res.data;
        
        // Debug: ver o formato da data que vem do backend
        console.log('Data recebida do backend:', data.resolution_expires_at);
        
        // Mapear dados da API para o novo modelo
        this.form.name = data.name || '';
        this.form.cnpj = data.cnpj || '';
        this.form.address = data.address || '';
        this.form.city = data.city || '';
        this.form.uf = data.uf || '';
        this.form.responsible = data.responsible || '';
        this.form.email = data.email || '';
        this.form.phone = data.phone || '';
        this.form.inep_code = data.inep_code || '';
        this.form.resolution = data.resolution || '';
        // Para campos de data, formatar para o input type="date" (YYYY-MM-DD)
        this.form.resolution_expires_at = this.formatDateForInput(data.resolution_expires_at);
        
        console.log('Data formatada para o input:', this.form.resolution_expires_at);
        
        // Mapear imagens existentes
        if (data.colored_logo) {
          this.$set(this.form.imagens, 'logoColorida', data.colored_logo);
        }
        if (data.bw_logo) {
          this.$set(this.form.imagens, 'logoPretoBranco', data.bw_logo);
        }
        if (data.certificate_front_background) {
          this.$set(this.form.imagens, 'certificadoEjaFrente', data.certificate_front_background);
        }
        if (data.certificate_back_background) {
          this.$set(this.form.imagens, 'certificadoEjaVerso', data.certificate_back_background);
        }
        if (data.certificate_technical_front_background) {
          this.$set(this.form.imagens, 'certificadoTecnicoFrente', data.certificate_technical_front_background);
        }
        if (data.certificate_technical_back_background) {
          this.$set(this.form.imagens, 'certificadoTecnicoVerso', data.certificate_technical_back_background);
        }
        if (data.history_front_background) {
          this.$set(this.form.imagens, 'historicoFrente', data.history_front_background);
        }
        if (data.history_back_background) {
          this.$set(this.form.imagens, 'historicoVerso', data.history_back_background);
        }
        if (data.certificate_free_front_background) {
          this.$set(this.form.imagens, 'cursosLivresFrente', data.certificate_free_front_background);
        }
        if (data.certificate_free_back_background) {
          this.$set(this.form.imagens, 'cursosLivresVerso', data.certificate_free_back_background);
        }
      } catch (error) {
        Toast.fire("Erro", error.response?.data?.message || "Erro ao carregar dados.", "error");
      }
    },
    async salvar() {
      this.loading = true;
      const fd = this.buildFormData();

      try {
        let res;
        if (this.isEdit) {
          fd.append("_method", "PUT");
          res = await api.post(`/branches/${this.id}`, fd);
        } else {
          res = await api.post("/branches", fd);
        }
        Toast.fire(res.data.message || "Filial salva com sucesso!", "", "success");
        this.$router.push({ name: "branches" });
      } catch (error) {
        const data = error.response?.data;
        let message = data?.message || "Erro ao salvar filial.";
        if (data?.errors) {
          message = Object.values(data.errors).flat().join("\n");
        }
        Toast.fire("Erro", message, "error");
      } finally {
        this.loading = false;
      }
    },
    voltar() {
      this.$router.go(-1);
    },
  },
  mounted() {
    if (this.isEdit) {
      this.getItem();
    }
  },
};
</script>

<style scoped>
/* Variáveis CSS - usar apenas as variáveis do sistema */
:root {
  --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

/* Página */
.branch-form-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  /* background: var(--color-background-tertiary, #f5f5f5); */
  min-height: 100vh;
  font-family: var(--font-sans);
}

/* Header da Página */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 16px 20px;
  background: var(--color-background-primary, #ffffff);
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  border-radius: var(--border-radius-lg, 12px);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--color-text-primary, #1a1a1a);
  margin: 0;
}

.page-subtitle {
  font-weight: 400;
  color: var(--color-text-secondary, #666666);
}

.uf-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: var(--color-background-info, #e3f2fd);
  color: var(--color-text-info, #1976d2);
  border-radius: var(--border-radius-md, 8px);
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* Footer da Página */
.page-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding: 16px 20px;
  background: var(--color-background-primary, #ffffff);
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  border-radius: var(--border-radius-lg, 12px);
}

/* Seções */
.section {
  background: var(--color-background-primary, #ffffff);
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  border-radius: var(--border-radius-lg, 12px);
  margin-bottom: 16px;
  overflow: hidden;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  background: var(--color-background-secondary, #fafafa);
}

.section-head i {
  font-size: 16px;
  color: var(--color-text-secondary, #666666);
}

.section-head h3 {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary, #666666);
  margin: 0;
}

.section-body {
  padding: 20px;
}

/* Grids */
.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* Campos de formulário */
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary, #666666);
}

.field input,
.field textarea,
.field select {
  background: var(--color-background-secondary, #fafafa);
  border: 0.5px solid var(--color-border-secondary, #d0d0d0);
  border-radius: var(--border-radius-md, 8px);
  padding: 7px 10px;
  font-size: 13px;
  color: var(--color-text-primary, #1a1a1a);
  width: 100%;
  font-family: var(--font-sans);
  transition: border-color 0.2s;
}

.field input:focus,
.field textarea:focus,
.field select:focus {
  outline: none;
  border-color: var(--color-text-info, #1976d2);
}

.field textarea {
  resize: vertical;
  min-height: 52px;
}

/* Subseções */
.subsection {
  margin-bottom: 24px;
}

.subsection:last-child {
  margin-bottom: 0;
}

.subsection-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-primary, #1a1a1a);
  margin: 0 0 16px 0;
}

/* Slots de Imagem */
.image-slot {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-slot label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary, #666666);
}

.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 72px;
  border: 1px dashed var(--color-border-tertiary, #e0e0e0);
  border-radius: var(--border-radius-md, 8px);
  background: var(--color-background-secondary, #fafafa);
  overflow: hidden;
}

.image-preview-small {
  height: 56px;
}

.image-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-preview i {
  font-size: 24px;
  color: var(--color-text-tertiary, #999999);
}

.image-actions {
  display: flex;
  gap: 8px;
}

.image-constraint {
  font-size: 10px;
  color: var(--color-text-tertiary, #999999);
}

.badge-optional {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  background: var(--color-background-secondary, #fafafa);
  color: var(--color-text-tertiary, #999999);
  border-radius: 4px;
  margin-left: 6px;
}

/* Grid de Documentos */
.document-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* Cards de Documentos */
.document-card {
  border: 0.5px solid var(--color-border-tertiary, #e0e0e0);
  border-radius: var(--border-radius-md, 8px);
  overflow: hidden;
  background: var(--color-background-primary, #ffffff);
}

.document-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--color-background-secondary, #fafafa);
  border-bottom: 0.5px solid var(--color-border-tertiary, #e0e0e0);
}

.document-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-primary, #1a1a1a);
}

.document-card-title i {
  font-size: 14px;
  color: var(--color-text-secondary, #666666);
}

.document-badge {
  font-size: 9px;
  font-weight: 500;
  padding: 3px 8px;
  background: var(--color-background-info, #e3f2fd);
  color: var(--color-text-info, #1976d2);
  border-radius: 4px;
  text-transform: uppercase;
}

.document-card-body {
  padding: 16px;
}

/* Card de Adição */
.document-card-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border: 1px dashed var(--color-border-secondary, #d0d0d0);
  background: var(--color-background-secondary, #fafafa);
  cursor: pointer;
  transition: all 0.2s;
}

.document-card-add:hover {
  border-color: var(--color-text-info, #1976d2);
  background: var(--color-background-primary, #ffffff);
}

.document-card-add i {
  font-size: 32px;
  color: var(--color-text-tertiary, #999999);
  margin-bottom: 8px;
}

.document-card-add span {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary, #666666);
}

/* Botões */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: var(--border-radius-md, 8px);
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--font-sans);
}

.btn i {
  font-size: 16px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-text-info, #1976d2);
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background: #1565c0;
}

.btn-secondary {
  background: var(--color-background-secondary, #fafafa);
  color: var(--color-text-primary, #1a1a1a);
  border: 0.5px solid var(--color-border-secondary, #d0d0d0);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-background-primary, #ffffff);
  border-color: var(--color-border-primary, #b0b0b0);
}

.btn-danger {
  background: var(--color-text-danger, #d32f2f);
  color: #ffffff;
}

.btn-danger:hover:not(:disabled) {
  background: #c62828;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 11px;
}

.btn-sm i {
  font-size: 14px;
}

/* Responsividade */
@media (max-width: 768px) {
  .branch-form-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .grid-3 {
    grid-template-columns: 1fr;
  }

  .grid-3 .field[style*="span 2"] {
    grid-column: span 1 !important;
  }

  .grid-2 {
    grid-template-columns: 1fr;
  }

  .document-grid {
    grid-template-columns: 1fr;
  }
}
</style>
